import {
  CREDIT_ACTIVITIES,
  CREDIT_RATES,
  type Account,
  type CreditActivity,
  type UsageBreakdown,
} from '@lyrmo/shared';
import { useEffect, useId, useRef, useState, type CSSProperties, type ReactNode } from 'react';
import { Icon, type IconName } from '../../components/Icon';
import { useCountingNumber } from '../../hooks/useCountingNumber';
import { usePresence } from '../../hooks/usePresence';
import { creditRateTable } from '../../i18n/credit-rates';
import { useI18n, type I18n } from '../../i18n/i18n-context';
import { rich } from '../../i18n/rich';
import { cx } from '../../lib/cx';
import { useAccount } from './account-context';
import { usageLevel, usedShare } from './usage-level';
import { UsageSummary } from './UsageSummary';
import './account.css';

/** Each activity's icon, and its row in the credit rates (credit-rates.ts), for its name. */
const ACTIVITIES: Readonly<
  Record<
    CreditActivity,
    { icon: IconName; rate: 'text' | 'tools' | 'dictation' | 'speech' | 'imageText' }
  >
> = {
  rewrite: { icon: 'letters', rate: 'text' },
  tool: { icon: 'wand', rate: 'tools' },
  dictation: { icon: 'microphone', rate: 'dictation' },
  speech: { icon: 'speaker', rate: 'speech' },
  imageText: { icon: 'image', rate: 'imageText' },
};

/** Each activity's credits, and what they're charged for: words, audio seconds, or images. */
const RATES: Readonly<
  Record<CreditActivity, { credits: number; per: 'words' | 'seconds' | 'images' }>
> = {
  rewrite: { credits: CREDIT_RATES.rewritePerWord, per: 'words' },
  tool: { credits: CREDIT_RATES.toolPerWord, per: 'words' },
  dictation: { credits: CREDIT_RATES.transcriptionPerSecond, per: 'seconds' },
  speech: { credits: CREDIT_RATES.speechPerWord, per: 'words' },
  imageText: { credits: CREDIT_RATES.imagePerImage, per: 'images' },
};

type Breakdown =
  | { readonly state: 'loading' }
  | { readonly state: 'failed'; readonly message: string }
  | { readonly state: 'ready'; readonly breakdown: UsageBreakdown };

/**
 * What today's credits went on, each activity's share of them as a bar, and what the credits
 * left would pay for: the credits breakdown's body.
 */
function CreditBreakdown({ usage, breakdown }: { usage: Account['usage']; breakdown: Breakdown }) {
  const i18n = useI18n();
  const { t, format } = i18n;
  const names = Object.fromEntries(
    creditRateTable(i18n).rows.map(({ id, label }) => [id, label]),
  ) as Record<(typeof ACTIVITIES)[CreditActivity]['rate'], string>;

  let spent: ReactNode;
  if (breakdown.state === 'failed') {
    spent = (
      <p className="usage-breakdown-note" role="status">
        {breakdown.message}
      </p>
    );
  } else {
    const ready = breakdown.state === 'ready' ? breakdown.breakdown : null;
    // What wasn't itemized: spent before it was, or by others on a visitor's network.
    const itemized = ready
      ? CREDIT_ACTIVITIES.reduce((sum, activity) => sum + ready.spent[activity], 0)
      : 0;
    const other = ready ? Math.max(0, ready.usage.used - itemized) : 0;
    const used = ready?.usage.used ?? 0;
    const rows = [
      ...CREDIT_ACTIVITIES.map((activity) => ({
        id: activity,
        icon: ACTIVITIES[activity].icon,
        name: names[ACTIVITIES[activity].rate],
        credits: ready?.spent[activity] ?? 0,
      })),
      ...(other > 0
        ? [{ id: 'other', icon: 'clock' as const, name: t.account.otherUse, credits: other }]
        : []),
    ];
    spent = (
      <ul className="usage-breakdown-rows" aria-busy={!ready}>
        {rows.map(({ id, icon, name, credits }) => (
          <li
            key={id}
            className={cx('usage-breakdown-row', ready && credits === 0 && 'is-zero')}
            style={{ '--share': used > 0 ? credits / used : 0 } as CSSProperties}
          >
            <span className="usage-breakdown-name">
              <Icon name={icon} />
              {name}
            </span>
            <span className="usage-breakdown-bar" aria-hidden="true">
              <span />
            </span>
            <span className="usage-breakdown-credits">
              {ready ? format.number(credits) : <span className="skeleton-line" />}
            </span>
          </li>
        ))}
      </ul>
    );
  }

  return (
    <>
      <UsageSummary usage={usage} />
      <section className="usage-breakdown" aria-label={t.account.spentOn}>
        <h3 className="usage-breakdown-heading">{t.account.spentOn}</h3>
        {spent}
      </section>
      {usage.remaining > 0 && (
        <section className="usage-breakdown" aria-label={t.account.creditsBuy(usage.remaining)}>
          <h3 className="usage-breakdown-heading">{t.account.creditsBuy(usage.remaining)}</h3>
          <ul className="usage-buys">
            {CREDIT_ACTIVITIES.map((activity) => {
              const { credits, per } = RATES[activity];
              const amount = Math.floor(usage.remaining / credits);
              return (
                <li key={activity}>
                  <Icon name={ACTIVITIES[activity].icon} />
                  <span className="usage-buys-name">{names[ACTIVITIES[activity].rate]}</span>
                  <span className="usage-buys-words">
                    ≈ {per === 'seconds' ? format.duration(amount) : t.counts[per](amount)}
                  </span>
                </li>
              );
            })}
          </ul>
        </section>
      )}
    </>
  );
}

/** The chip's plan and today's credits left, with a bar that empties as they're used. */
function ChipContents({ account }: { readonly account: Account }) {
  const { t, format } = useI18n();
  const { plan, usage } = account;
  // Credits count up to what's left as the chip appears, and down as they're spent, the bar
  // emptying with them.
  const remaining = useCountingNumber(usage.remaining);
  const shownUsed = usage.used + usage.remaining - remaining;
  return (
    <>
      <span className="plan-badge usage-chip-plan" data-plan={plan}>
        {t.planNames[plan]}
      </span>
      <span className="usage-chip-words">
        {rich(t.account.chipLeft, {
          count: <strong>{format.number(remaining)}</strong>,
          unit: <span className="usage-chip-unit">{t.account.chipUnit(usage.remaining)}</span>,
          when: <span className="usage-chip-when">{t.account.chipWhen}</span>,
        })}
      </span>
      <span className="usage-chip-meter" aria-hidden="true">
        <span
          style={{ width: `${String((1 - usedShare({ ...usage, used: shownUsed })) * 100)}%` }}
        />
      </span>
    </>
  );
}

const chipTitle = ({ usage }: Account, { t, format }: I18n) =>
  t.account.chipTitle(
    format.number(usage.used),
    format.number(usage.limit),
    format.resetTime(usage.resetsAt),
  );

/** The chip opening the credits breakdown: what they went on, and what's left would pay for. */
function UsageMenu({ account }: { readonly account: Account }) {
  const i18n = useI18n();
  const { t, describeError } = i18n;
  const { client, openDialog } = useAccount();
  const { plan, usage } = account;
  const [open, setOpen] = useState(false);
  const { present, leaving } = usePresence(open);
  const [breakdown, setBreakdown] = useState<Breakdown>({ state: 'loading' });
  const rootRef = useRef<HTMLDivElement>(null);
  const triggerRef = useRef<HTMLButtonElement>(null);
  const panelId = useId();

  // Asked for each time it opens, and again as credits are spent meanwhile.
  useEffect(() => {
    if (!open) return;
    let current = true;
    client.getUsageBreakdown().then(
      (loaded) => {
        if (current) setBreakdown({ state: 'ready', breakdown: loaded });
      },
      (error: unknown) => {
        if (current) setBreakdown({ state: 'failed', message: describeError(error) });
      },
    );
    return () => {
      current = false;
    };
  }, [client, describeError, open, usage.used]);

  useEffect(() => {
    if (!open) return;
    const onPointerDown = (event: PointerEvent) => {
      if (event.target instanceof Node && rootRef.current?.contains(event.target)) return;
      setOpen(false);
    };
    const onKeyDown = (event: KeyboardEvent) => {
      if (event.key !== 'Escape') return;
      // Captured first, so the editor's own Escape (which leaves focus mode) never sees it.
      event.stopPropagation();
      setOpen(false);
      triggerRef.current?.focus();
    };
    document.addEventListener('pointerdown', onPointerDown);
    document.addEventListener('keydown', onKeyDown, true);
    return () => {
      document.removeEventListener('pointerdown', onPointerDown);
      document.removeEventListener('keydown', onKeyDown, true);
    };
  }, [open]);

  function showPlans() {
    setOpen(false);
    // The dialog hands focus back to whatever had it, so make that the chip.
    triggerRef.current?.focus();
    openDialog('plans', 'usage');
  }

  return (
    <div
      className="usage-menu"
      ref={rootRef}
      onBlur={(event) => {
        // Tabbing out of the breakdown closes it.
        if (!event.currentTarget.contains(event.relatedTarget)) setOpen(false);
      }}
    >
      <button
        ref={triggerRef}
        type="button"
        className="usage-chip"
        data-level={usageLevel(usage)}
        aria-expanded={open}
        aria-controls={panelId}
        aria-haspopup="dialog"
        title={chipTitle(account, i18n)}
        onClick={() => {
          if (!open) setBreakdown({ state: 'loading' });
          setOpen((current) => !current);
        }}
      >
        <ChipContents account={account} />
      </button>

      {present && (
        <div
          className={cx('account-panel usage-panel', leaving && 'is-leaving')}
          id={panelId}
          role="dialog"
          aria-label={t.account.todaysCredits}
          inert={leaving}
        >
          <CreditBreakdown usage={usage} breakdown={breakdown} />
          <div className="usage-panel-actions">
            <span className="plan-badge" data-plan={plan}>
              {t.planNames[plan]}
            </span>
            <button
              type="button"
              className={cx('button', plan === 'free' ? 'button--primary' : 'button--ghost')}
              onClick={showPlans}
            >
              <Icon name="sparkle" />
              {plan === 'free' ? t.account.upgradeForMore : t.billing.comparePlans}
            </button>
          </div>
        </div>
      )}
    </div>
  );
}

/**
 * The plan and today's credits left. On the site it opens the credits breakdown, with the plans
 * a step on; elsewhere, such as the desktop app, which has no plans to show, it only shows them.
 */
export function UsageChip({
  account,
  opensBreakdown = false,
}: {
  readonly account: Account;
  readonly opensBreakdown?: boolean;
}) {
  const i18n = useI18n();
  if (opensBreakdown) return <UsageMenu account={account} />;
  return (
    <p
      className="usage-chip"
      data-level={usageLevel(account.usage)}
      title={chipTitle(account, i18n)}
    >
      <ChipContents account={account} />
    </p>
  );
}
